// src/components/module/reportes/Charts/VentasChart.tsx
import React, { useContext } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Paper, Typography, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ShowChart, StackedLineChart } from '@mui/icons-material';
import { DarkModeContext } from '../../../../context/darkModeContext';
import { EstadisticaVentas } from '../../../types/reportes.types';
import { ReportesApi } from '../../../../services/api/reportesApi';
import './charts.scss';

interface VentasChartProps {
  data: EstadisticaVentas[];
  titulo?: string;
  mostrarMonto?: boolean;
  mostrarVentas?: boolean;
}

export const VentasChart: React.FC<VentasChartProps> = ({
  data,
  titulo = 'Tendencia de Ventas',
  mostrarMonto = true,
  mostrarVentas = true,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const [tipoGrafico, setTipoGrafico] = React.useState<'linea' | 'area'>('area');

  // ✅ Colores actualizados para mejor contraste en dark mode
  const colors = {
    primary: darkMode ? '#06b6d4' : '#0891b2',
    success: darkMode ? '#34d399' : '#10b981',
    grid: darkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(71, 85, 105, 0.15)',
    text: darkMode ? '#94a3b8' : '#475569',
    background: darkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    border: darkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(8, 145, 178, 0.1)',
  };

  // Formatear datos para el gráfico
  const chartData = data.map((item) => ({
    fecha: new Date(item.fecha).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
    }),
    ventas: item.ventas,
    monto: item.monto,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={8}
          sx={{
            p: 2,
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            borderRadius: 2,
            boxShadow: darkMode
              ? '0 8px 24px rgba(0, 0, 0, 0.4)'
              : '0 8px 24px rgba(0, 0, 0, 0.15)',
            zIndex: 9999, // ✅ Tooltip siempre visible
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: darkMode ? '#f1f5f9' : '#0f172a',
              fontWeight: 600,
            }}
          >
            {payload[0].payload.fecha}
          </Typography>
          {mostrarVentas && (
            <Typography
              variant="body2"
              sx={{
                color: colors.primary,
                fontWeight: 500,
              }}
            >
              Ventas: {payload[0].value}
            </Typography>
          )}
          {mostrarMonto && payload[1] && (
            <Typography
              variant="body2"
              sx={{
                color: colors.success,
                fontWeight: 500,
              }}
            >
              Monto: {ReportesApi.formatearMoneda(payload[1].value)}
            </Typography>
          )}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper
      className="chart-container"
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: `${colors.background} !important`,
        backgroundImage: darkMode
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(30, 41, 59, 0.98) 100%) !important'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%) !important',
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: 0, // ✅ FIX: Z-index muy bajo
        isolation: 'isolate', // ✅ FIX: Crear contexto propio
        mt: 4, // ✅ FIX: Margin top 32px
        mb: 4, // ✅ FIX: Margin bottom 32px
      }}
    >
      <Box className="chart-header">
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            color: darkMode ? '#f1f5f9' : '#0f172a',
          }}
        >
          {titulo}
        </Typography>
        <ToggleButtonGroup
          value={tipoGrafico}
          exclusive
          onChange={(_, value) => value && setTipoGrafico(value)}
          size="small"
          sx={{
            position: 'relative',
            zIndex: 3, // ✅ Botones interactivos
            '& .MuiToggleButton-root': {
              color: darkMode ? '#94a3b8' : '#64748b',
              borderColor: darkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(8, 145, 178, 0.3)',
              '&.Mui-selected': {
                backgroundColor: darkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(8, 145, 178, 0.1)',
                color: darkMode ? '#06b6d4' : '#0891b2',
                borderColor: darkMode ? '#06b6d4' : '#0891b2',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(6, 182, 212, 0.25)' : 'rgba(8, 145, 178, 0.15)',
                }
              },
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(8, 145, 178, 0.05)',
              }
            }
          }}
        >
          <ToggleButton value="linea">
            <ShowChart fontSize="small" />
          </ToggleButton>
          <ToggleButton value="area">
            <StackedLineChart fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ResponsiveContainer
        width="100%"
        height={300}
        style={{
          position: 'relative',
          zIndex: 1 // ✅ Container con z-index controlado
        }}
      >
        {tipoGrafico === 'linea' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis
              dataKey="fecha"
              stroke={colors.text}
              style={{
                fontSize: '12px',
                fill: colors.text,
              }}
              tick={{ fill: colors.text }}
            />
            <YAxis
              stroke={colors.text}
              style={{
                fontSize: '12px',
                fill: colors.text,
              }}
              tick={{ fill: colors.text }}
            />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
            <Legend
              wrapperStyle={{
                paddingTop: '16px',
                position: 'relative',
                zIndex: 2,
              }}
              iconType="circle"
            />
            {mostrarVentas && (
              <Line
                type="monotone"
                dataKey="ventas"
                stroke={colors.primary}
                strokeWidth={3}
                dot={{
                  fill: colors.primary,
                  r: 5,
                  strokeWidth: 2,
                  stroke: darkMode ? '#0f172a' : '#ffffff',
                }}
                activeDot={{
                  r: 7,
                  fill: colors.primary,
                  stroke: darkMode ? '#0f172a' : '#ffffff',
                  strokeWidth: 2,
                }}
                name="Ventas"
              />
            )}
            {mostrarMonto && (
              <Line
                type="monotone"
                dataKey="monto"
                stroke={colors.success}
                strokeWidth={3}
                dot={{
                  fill: colors.success,
                  r: 5,
                  strokeWidth: 2,
                  stroke: darkMode ? '#0f172a' : '#ffffff',
                }}
                activeDot={{
                  r: 7,
                  fill: colors.success,
                  stroke: darkMode ? '#0f172a' : '#ffffff',
                  strokeWidth: 2,
                }}
                name="Monto"
              />
            )}
          </LineChart>
        ) : (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.primary} stopOpacity={0.8} />
                <stop offset="95%" stopColor={colors.primary} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorMonto" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.success} stopOpacity={0.8} />
                <stop offset="95%" stopColor={colors.success} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis
              dataKey="fecha"
              stroke={colors.text}
              style={{
                fontSize: '12px',
                fill: colors.text,
              }}
              tick={{ fill: colors.text }}
            />
            <YAxis
              stroke={colors.text}
              style={{
                fontSize: '12px',
                fill: colors.text,
              }}
              tick={{ fill: colors.text }}
            />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
            <Legend
              wrapperStyle={{
                paddingTop: '16px',
                position: 'relative',
                zIndex: 2,
              }}
              iconType="circle"
            />
            {mostrarVentas && (
              <Area
                type="monotone"
                dataKey="ventas"
                stroke={colors.primary}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorVentas)"
                name="Ventas"
              />
            )}
            {mostrarMonto && (
              <Area
                type="monotone"
                dataKey="monto"
                stroke={colors.success}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorMonto)"
                name="Monto"
              />
            )}
          </AreaChart>
        )}
      </ResponsiveContainer>

      {chartData.length === 0 && (
        <Box
          className="chart-empty-state"
          sx={{
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: darkMode ? 'rgba(71, 85, 105, 0.1)' : 'rgba(148, 163, 184, 0.05)',
            borderRadius: 2,
            border: `2px dashed ${darkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.2)'}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: darkMode ? '#94a3b8' : '#64748b',
            }}
          >
            No hay datos disponibles para mostrar
          </Typography>
        </Box>
      )}
    </Paper>
  );
};